import { Component } from "react";

import type { IComponent } from "../Component";

export enum AnimationType {
    Fade,
    SlideDown,
    SlideLeft,
    SlideRight,
    Custom,
}

export interface IAnimationApi extends IComponent {
    animationType?: AnimationType;
    duration?: number;
    animationClass?: string;
    children: JSX.Element | JSX.Element[] | null;
    onFinish?: () => void;
    cancelInitialAnimation?: boolean;
    overflowHidden?: boolean;
}

interface IAnimationApiState {
    children: JSX.Element[];
    initialMount?: boolean;
}

export class AnimationApi extends Component<IAnimationApi, IAnimationApiState> {
    public static defaultProps: IAnimationApi = {
        animationType: AnimationType.Fade,
        children: [],
        duration: 300,
        overflowHidden: true,
    };

    public static getDerivedStateFromProps(
        props: IAnimationApi,
        state: IAnimationApiState,
    ): IAnimationApiState {
        const propsChildren = AnimationApi.getArrayFromChildrenProps(props);

        let children: JSX.Element[] = [];

        const arr = [...propsChildren, ...state.children];
        const assoc: { [key: string]: boolean } = {};

        arr.forEach((item) => {
            if (item && !assoc[`${item.key}`]) {
                children = [...children, item];
                assoc[`${item.key}`] = true;
            }
        });

        return { children };
    }

    private static getArrayFromChildrenProps = (props: IAnimationApi): JSX.Element[] =>
        Array.isArray(props.children) ? props.children : (props.children && [props.children]) || [];

    public isPureComponent: boolean = true;

    private animation: { [key: string]: Animation } = {};

    private isUnmounted = false;

    private container: { [key: string]: HTMLDivElement } = {};

    constructor(props: IAnimationApi) {
        super(props);

        const children = AnimationApi.getArrayFromChildrenProps(props);

        this.state = {
            children,
            initialMount: props.cancelInitialAnimation,
        };
    }

    public componentDidMount() {
        if (!this.props.cancelInitialAnimation) {
            this.setState({ initialMount: false });
            AnimationApi.getArrayFromChildrenProps(this.props).forEach(
                (val) => val && this.runAnimation(true, `${val.key}`),
            );
        }
    }

    public componentDidUpdate(_prevProps: IAnimationApi, prevState: IAnimationApiState) {
        const propsList = AnimationApi.getArrayFromChildrenProps(this.props);

        this.state.children.forEach((c) => {
            if (
                c &&
                prevState.children.findIndex((val) => val && `${c.key}` === `${val.key}`) < 0
            ) {
                this.runAnimation(true, `${c.key}`);
            } else if (c && propsList.findIndex((val) => val && `${c.key}` === `${val.key}`) < 0) {
                this.runAnimation(false, `${c.key}`);
            }
        });
    }

    public componentWillUnmount() {
        this.isUnmounted = true;
    }

    private getAnimationOptions = (show: boolean): KeyframeAnimationOptions => ({
        direction: show ? "normal" : "reverse",
        duration: this.props.duration,
        fill: "forwards",
    });

    private resetDiv = (key: string): void => {
        if (!this.props.children || !this.container || !this.container[key]) {
            return;
        }

        this.container[key].animate(
            {
                height: ["100%", "100%"],
                opacity: ["1", "1"],
            } as PropertyIndexedKeyframes,
            { ...this.getAnimationOptions, duration: 1, fill: "forwards" },
        );
    };

    private setRef = (ref: HTMLDivElement | null, key: string) => {
        if (ref) {
            this.container[key] = ref;
        }
    };

    private animationKeyFrame = (key: string): PropertyIndexedKeyframes => {
        const keyFrame: PropertyIndexedKeyframes = {} as PropertyIndexedKeyframes;

        const animElement = this.container[key];

        if (!animElement || !animElement.firstElementChild) {
            return {};
        }

        switch (this.props.animationType) {
            case AnimationType.SlideDown:
                // eslint-disable-next-line no-case-declarations
                const height = animElement.firstElementChild.clientHeight;

                if (height !== 0) {
                    keyFrame.height = ["0px", `${height || 1000}px`];
                }

                break;
            case AnimationType.Fade:
            default:
                keyFrame.opacity = ["0", "1"];
                break;
        }

        return keyFrame;
    };

    private onFinish = (key: string) => {
        if (this.props.animationClass && this.container[key]) {
            this.container[key].classList.remove(this.props.animationClass);
        }

        if (
            AnimationApi.getArrayFromChildrenProps(this.props).findIndex(
                (c) => c && `${c.key}` === key,
            ) < 0
        ) {
            this.setState((state) => ({
                children: state.children.filter((c) => c && `${c.key}` !== key),
            }));
        } else {
            this.resetDiv(key);
        }

        if (this.props.onFinish) {
            this.props.onFinish();
        }
    };

    private runAnimation(show: boolean, key: string): void {
        if (this.props.animationClass && this.container[key]) {
            this.container[key].classList.add(this.props.animationClass);
        }

        if (this.props.animationType === AnimationType.Custom) {
            return;
        }

        this.animation[key] = this.container[key].animate(
            this.animationKeyFrame(key),
            this.getAnimationOptions(show),
        );

        this.animation[key].onfinish = () => !this.isUnmounted && this.onFinish(key);
    }

    private divProps(): {
        className: string | undefined;
        id: string | undefined;
        style: React.CSSProperties;
    } {
        return {
            className: this.props.className,
            id: this.props.clientId,
            style: {
                overflow: this.props.overflowHidden === false ? undefined : "hidden",
                ...this.props.style,
            },
        };
    }

    public render() {
        this.container = {};
        const divProps = this.divProps();

        if (!this.state.initialMount) {
            // eslint-disable-next-line default-case
            switch (this.props.animationType) {
                case AnimationType.Fade:
                    divProps.style.opacity = undefined;
                    break;
                case AnimationType.SlideDown:
                    divProps.style.height = 0;
                    break;
                case AnimationType.SlideLeft:
                case AnimationType.SlideRight:
                    divProps.style.width = 0;
                    break;
            }
        }

        return this.state.children.map((val) => (
            <div {...divProps} key={`${val.key}`} ref={(ref) => this.setRef(ref, `${val.key}`)}>
                {val}
            </div>
        ));
    }
}
